import { Link } from "react-router-dom";
import { useEffect } from "react";
import Logo from "../components/Logo";
import MartialVisual from "../components/MartialVisual";
import { BRAND_ASSETS, EVENT_INFO } from "../lib/brand";
import { injectJsonLd } from "../lib/jsonld";
import { EnvelopeSimple, ChartLineUp, Megaphone, Barbell, ArrowRight, Lightning } from "@phosphor-icons/react";

const modules = [
  { icon: EnvelopeSimple, title: "Courriels IA", desc: "Rédige et fait le suivi de tes courriels automatiquement. Détecte la tonalité et propose la bonne réponse aux parents et clients.", tag: "01" },
  { icon: ChartLineUp, title: "Finances", desc: "Tableau de bord perso + compagnie. Stratégies IA pour rembourser tes dettes et faire fructifier ton argent. TPS/TVQ inclus.", tag: "02" },
  { icon: Megaphone, title: "Marketing", desc: "Campagnes adaptées par plateforme : LinkedIn pro, Facebook communauté, TikTok punchy. Avatar IA inclus.", tag: "03" },
  { icon: Barbell, title: "Circuit Athlètes", desc: "Carnet santé, blessures, performances. Kickboxing, K-1 et Muay Thaï. De la relève provinciale au mondial.", tag: "04" },
];

export default function Landing() {
  useEffect(() => {
    injectJsonLd("schema-org", {
      "@context": "https://schema.org",
      "@type": "MartialArtsSchool",
      "name": "Institut Impact Élite Martial",
      "url": typeof window !== "undefined" ? window.location.origin : "",
      "description": "École de Muay Thaï, Kickboxing et K-1 fondée par Ralph David Jean-Pierre, Kru certifié Lumpinee Stadium et officiel Muay Thai Canada.",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Val-d'Or",
        "addressRegion": "QC",
        "addressCountry": "CA",
      },
      "founder": {
        "@type": "Person",
        "name": "Ralph David Jean-Pierre",
        "jobTitle": "Kru, Officiel certifié Lumpinee Stadium",
      },
      "sport": ["Muay Thai", "Kickboxing", "K-1"],
    });
  }, []);
  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 hard-border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo/>
          <div className="flex items-center gap-3">
            <Link to="/boutique-accueil" className="ticker text-muted-foreground hover:text-foreground" data-testid="nav-boutique">Boutique</Link>
            <Link to="/membres" className="ticker text-muted-foreground hover:text-foreground" data-testid="nav-membres">Espace Membres</Link>
            <Link to="/franchise" className="ticker text-muted-foreground hover:text-foreground" data-testid="nav-franchise">Franchise KAIZEN</Link>
            <Link to="/auth" className="ticker text-muted-foreground hover:text-foreground" data-testid="nav-login">Connexion</Link>
            <Link to="/auth?mode=register" className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2.5 ticker transition-colors" data-testid="nav-register">
              Commencer →
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 fade-up">
            <div className="chip mb-6"><Lightning size={12} weight="fill"/> Québec · Fait en IA</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
              L'assistant qui gère<br/>
              <span className="text-[#D90429]">tes courriels,</span><br/>
              <span className="underline decoration-[#D90429] decoration-4 underline-offset-[10px]">tes finances</span> et<br/>
              ta relève sportive.
            </h1>
            <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">
              Une seule app pour bâtir ton empire : réponses courriels automatiques, gestion financière intelligente,
              marketing piloté par IA et gestion complète du <strong className="text-foreground">Réseau KAIZEN</strong> — avec carnet santé des athlètes.
            </p>
            <div className="mt-10 flex items-center gap-4">
              <Link to="/auth?mode=register" data-testid="hero-start-btn" className="bg-[#D90429] hover:bg-[#BF0424] text-white px-7 py-4 flex items-center gap-2 transition-colors">
                <span className="ticker">Démarrer maintenant</span>
                <ArrowRight size={18} weight="bold"/>
              </Link>
              <Link to="/auth" data-testid="hero-demo-btn" className="px-7 py-4 hard-border hover:bg-secondary transition-colors ticker">
                Essayer la démo
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-8 text-xs font-mono text-muted-foreground">
              <div><span className="text-foreground font-semibold">04</span> MODULES</div>
              <div><span className="text-foreground font-semibold">07 NOV</span> COMP. KAIZEN 2026</div>
              <div><span className="text-foreground font-semibold">CAD $</span> COMPTABILITÉ</div>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative h-full min-h-[420px] hard-border overflow-hidden bg-gradient-to-br from-zinc-950 to-black">
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-10 text-white">
                <div className="ticker text-amber-400 mb-3 text-xs tracking-[0.3em]">RÉSEAU KAIZEN · INTER-ÉCOLES</div>
                <div className="font-display font-black text-5xl lg:text-6xl tracking-tight mb-2">MINI<br/>COMPÉTITION<br/><span className="text-[#D90429]">KAIZEN</span></div>
                <div className="ticker text-white/60 mt-4">Muay Thaï · Kickboxing · JJB · Can Ryu</div>
                <div className="mt-6 inline-block border border-white/20 px-4 py-2">
                  <div className="ticker text-white/60 text-[10px]">PROCHAINE DATE OFFICIELLE</div>
                  <div className="font-display font-bold text-2xl mt-1">07 NOV 2026</div>
                  <div className="text-xs text-white/60 mt-1">Val-d'Or · Abitibi-Témiscamingue</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="hard-border-t">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="ticker text-muted-foreground mb-2">— Les quatre modules</div>
              <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tight max-w-2xl">Une suite complète pour diriger. Sans quitter l'onglet.</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 hard-border">
            {modules.map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={i} data-testid={`module-card-${i}`} className={`p-8 lg:p-10 ${i % 2 === 0 ? 'md:border-r border-border' : ''} ${i < 2 ? 'border-b border-border' : ''} group hover:bg-secondary/40 transition-colors`}>
                  <div className="flex items-start justify-between mb-6">
                    <Icon size={36} weight="duotone" className="text-[#D90429]" />
                    <span className="ticker text-muted-foreground">{m.tag}</span>
                  </div>
                  <h3 className="font-display font-bold text-2xl mb-3">{m.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{m.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Showcase strip */}
      <section className="hard-border-t bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="ticker text-white/60 mb-3">— Réseau franchise KAIZEN</div>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tight">Bâtir le réseau d'écoles KAIZEN.<br/><span className="text-[#D90429]">Muay Thaï · Kickboxing · JJB · Can Ryu.</span></h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed">
              De la formation locale jusqu'au circuit national. Mini compétitions inter-écoles 2× par an,
              plan de commandites structuré, carnets athlètes connectés, marketing kit automatisé.
              Méthode Kaizen — un degré chaque jour.
            </p>
            <Link to="/auth?mode=register" className="inline-flex mt-8 items-center gap-2 bg-white text-black px-6 py-3 ticker hover:bg-[#D90429] hover:text-white transition-colors" data-testid="showcase-cta">
              Rejoindre le réseau <ArrowRight size={16} weight="bold"/>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="hard-border bg-gradient-to-br from-amber-500/20 to-amber-700/20 p-6 h-64 flex flex-col justify-between">
              <div className="ticker text-amber-300 text-[10px] tracking-[0.3em]">PROCHAINE COMP.</div>
              <div>
                <div className="font-display font-black text-4xl text-white">07 NOV<br/>2026</div>
                <div className="ticker text-white/60 text-xs mt-2">Val-d'Or · QC</div>
              </div>
            </div>
            <div className="hard-border bg-gradient-to-br from-purple-500/20 to-fuchsia-700/20 p-6 h-64 flex flex-col justify-between">
              <div className="ticker text-purple-300 text-[10px] tracking-[0.3em]">COMMANDITES</div>
              <div>
                <div className="font-display font-black text-4xl text-white">5<br/>NIVEAUX</div>
                <div className="ticker text-white/60 text-xs mt-2">Platine · Or · Argent · Bronze · Partenaire</div>
              </div>
            </div>
            <div className="col-span-2 hard-border bg-gradient-to-br from-zinc-900 to-black p-8 text-center">
              <div className="ticker text-[#D90429] text-[10px] tracking-[0.3em] mb-2">RÉSEAU INTER-ÉCOLES</div>
              <div className="font-display font-black text-3xl">3+ DOJOS · MULTI-DISCIPLINES</div>
              <div className="ticker text-white/60 text-xs mt-2">Val-d'Or · Rouyn-Noranda · écoles externes invitées</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="hard-border-t">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-4 ticker text-muted-foreground">
          <span>© 2026 KAIZEN SYSTEM · Institut Impact Élite Martial · Québec</span>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="hover:text-foreground" data-testid="footer-privacy">Confidentialité</Link>
            <Link to="/terms-of-service" className="hover:text-foreground" data-testid="footer-terms">Conditions d'utilisation</Link>
            <Link to="/data-deletion" className="hover:text-foreground" data-testid="footer-data-deletion">Suppression des données</Link>
            <Link to="/auth" className="hover:text-foreground">Se connecter →</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
